import { z } from 'zod';

/**
 * `defer-llm-org-byok` — public shapes for org-level LLM BYOK rows.
 *
 * Endpoints:
 *   GET    /api/orgs/:orgId/llm-credentials
 *   PUT    /api/orgs/:orgId/llm-credentials/:provider  (upsert)
 *   DELETE /api/orgs/:orgId/llm-credentials/:provider
 *
 * Authorisation: org admin only for writes; any member for reads
 * (but the key itself never leaves the server — only `keyHint`).
 */

export const ORG_LLM_PROVIDERS = ['openai', 'groq', 'anthropic'] as const;
export type OrgLlmProvider = (typeof ORG_LLM_PROVIDERS)[number];
export const OrgLlmProviderSchema = z.enum(ORG_LLM_PROVIDERS);

/**
 * Wire shape returned by GET / PUT. `keyHint` is the only public
 * preview of the key (typically the last 4 characters prefixed
 * with `…`) — the encrypted key itself stays on the server.
 */
export const OrgLlmCredentialSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  provider: OrgLlmProviderSchema,
  keyHint: z.string().max(40),
  model: z.string().max(120),
  baseUrl: z.string().max(255),
  isActive: z.boolean(),
  updatedBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OrgLlmCredential = z.infer<typeof OrgLlmCredentialSchema>;

export const ListOrgLlmCredentialsResponseSchema = z.object({
  credentials: z.array(OrgLlmCredentialSchema),
});
export type ListOrgLlmCredentialsResponse = z.infer<typeof ListOrgLlmCredentialsResponseSchema>;

/**
 * Upsert body for `PUT /api/orgs/:orgId/llm-credentials/:provider`.
 *
 * `apiKey` is plaintext on the wire (HTTPS only) and is encrypted
 * at rest using the same `encryption.ts` helper that
 * `user_api_keys` uses. `model` and `baseUrl` are optional — empty
 * strings tell the resolver to fall through to the platform default.
 */
export const UpsertOrgLlmCredentialRequestSchema = z
  .object({
    apiKey: z.string().min(8).max(2000),
    model: z.string().max(120).optional(),
    baseUrl: z.string().max(255).optional(),
  })
  .strict();
export type UpsertOrgLlmCredentialRequest = z.infer<typeof UpsertOrgLlmCredentialRequestSchema>;
